import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import "@/App.css";
import { AuthProvider } from "@/contexts/AuthContext";
import { BranchProvider } from "@/contexts/BranchContext";
import { SubscriberAuthProvider } from "@/contexts/SubscriberAuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import SubscriberProtectedRoute from "@/components/SubscriberProtectedRoute";
import api from "@/lib/api";
import { applyBranding } from "@/lib/utils-app";
import { mergePublicSiteForEditor } from "@/lib/gymSiteDefaults";
import { applyTheme } from "@/lib/gymThemes";

import Home from "@/pages/public/Home";
import ProductsPage from "@/pages/public/ProductsPage";
import EventsPage from "@/pages/public/EventsPage";
import PackagesPage from "@/pages/public/PackagesPage";
import ContactPage from "@/pages/public/ContactPage";
import PaymentSuccess from "@/pages/public/PaymentSuccess";
import PublicLayout from "@/pages/public/PublicLayout";

import AdminLogin from "@/pages/admin/AdminLogin";
import AdminLayout from "@/pages/admin/AdminLayout";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminProducts from "@/pages/admin/AdminProducts";
import AdminPackages from "@/pages/admin/AdminPackages";
import AdminSubscribers from "@/pages/admin/AdminSubscribers";
import AdminSubscriberDetail from "@/pages/admin/AdminSubscriberDetail";
import AdminPayments from "@/pages/admin/AdminPayments";
import AdminEvents from "@/pages/admin/AdminEvents";
import AdminMessages from "@/pages/admin/AdminMessages";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminPublicSite from "@/pages/admin/AdminPublicSite";
import AdminShifts from "@/pages/admin/AdminShifts";
import AdminAttendance from "@/pages/admin/AdminAttendance";
import AdminPaymentReturn from "@/pages/admin/AdminPaymentReturn";
import AdminSubscriptionReturn from "@/pages/admin/AdminSubscriptionReturn";
import AdminBranches from "@/pages/admin/AdminBranches";
import AdminBranchAdmins from "@/pages/admin/AdminBranchAdmins";
import SubscriberLayout from "@/pages/subscriber/SubscriberLayout";
import SubscriberLogin from "@/pages/subscriber/SubscriberLogin";
import SubscriberProfile from "@/pages/subscriber/SubscriberProfile";
import SubscriberProgress from "@/pages/subscriber/SubscriberProgress";
import SubscriberGallery from "@/pages/subscriber/SubscriberGallery";
import SubscriberFights from "@/pages/subscriber/SubscriberFights";
import SubscriberPayments from "@/pages/subscriber/SubscriberPayments";
import SubscriberPaymentReturn from "@/pages/subscriber/SubscriberPaymentReturn";
import { SuperAuthProvider } from "@/contexts/SuperAuthContext";
import SuperProtectedRoute from "@/components/SuperProtectedRoute";
import SuperLogin from "@/pages/super/SuperLogin";
import SuperLayout from "@/pages/super/SuperLayout";
import SuperSettings from "@/pages/super/SuperSettings";
import SuperIndex from "@/pages/super/SuperIndex";
import SuperBootstrap from "@/pages/super/SuperBootstrap";
import SuperClient from "@/pages/super/SuperClient";
import SuperBilling from "@/pages/super/SuperBilling";
import SuperSystemReset from "@/pages/super/SuperSystemReset";
import AsistenciaProtectedRoute from "@/components/AsistenciaProtectedRoute";
import AsistenciaCheckIn from "@/pages/asistencia/AsistenciaCheckIn";

function App() {
  const applyAppSettings = (settings = {}) => {
    applyTheme(settings.theme);
    applyBranding(settings);
  };

  useEffect(() => {
    Promise.all([
      api.get("/settings").catch(() => ({ data: {} })),
      api.get("/public-site").catch(() => ({ data: null })),
    ]).then(([settingsRes, siteRes]) => {
      const settings = settingsRes?.data || {};
      const site = mergePublicSiteForEditor(siteRes?.data);
      applyAppSettings(settings);
      applyBranding({ gym_name: site.gym_name, logo: site.logo });
    });

    const onSettingsUpdated = (event) => {
      applyAppSettings(event?.detail || {});
    };
    const onPublicSiteUpdated = (event) => {
      const site = mergePublicSiteForEditor(event?.detail);
      applyBranding({ gym_name: site.gym_name, logo: site.logo });
    };
    window.addEventListener("app-settings-updated", onSettingsUpdated);
    window.addEventListener("public-site-updated", onPublicSiteUpdated);
    return () => {
      window.removeEventListener("app-settings-updated", onSettingsUpdated);
      window.removeEventListener("public-site-updated", onPublicSiteUpdated);
    };
  }, []);

  return (
    <AuthProvider>
      <SuperAuthProvider>
      <SubscriberAuthProvider>
        <BrowserRouter>
          <Toaster theme="dark" position="top-right" richColors />
          <Routes>
            <Route element={<PublicLayout />}>
              <Route path="/" element={<Home />} />
              <Route path="/productos" element={<ProductsPage />} />
              <Route path="/eventos" element={<EventsPage />} />
              <Route path="/paquetes" element={<PackagesPage />} />
              <Route path="/contacto" element={<ContactPage />} />
              <Route path="/payment-success" element={<PaymentSuccess />} />
            </Route>

            <Route path="/admin/login" element={<AdminLogin />} />
            <Route
              path="/asistencia"
              element={
                <AsistenciaProtectedRoute>
                  <AsistenciaCheckIn />
                </AsistenciaProtectedRoute>
              }
            />
            <Route path="/super/login" element={<SuperLogin />} />
            <Route
              path="/super"
              element={
                <SuperProtectedRoute>
                  <SuperLayout />
                </SuperProtectedRoute>
              }
            >
              <Route index element={<SuperIndex />} />
              <Route path="inicio" element={<SuperBootstrap />} />
              <Route path="cliente" element={<SuperClient />} />
              <Route path="suscripcion" element={<SuperBilling />} />
              <Route path="modulos" element={<SuperSettings />} />
              <Route path="reinicio" element={<SuperSystemReset />} />
            </Route>
            <Route
              path="/admin"
              element={
                <ProtectedRoute>
                  <BranchProvider>
                    <AdminLayout />
                  </BranchProvider>
                </ProtectedRoute>
              }
            >
              <Route index element={<AdminDashboard />} />
              <Route path="sucursales" element={<AdminBranches />} />
              <Route path="usuarios-sucursal" element={<AdminBranchAdmins />} />
              <Route path="productos" element={<AdminProducts />} />
              <Route path="paquetes" element={<AdminPackages />} />
              <Route path="suscriptores" element={<AdminSubscribers />} />
              <Route path="suscriptores/:id" element={<AdminSubscriberDetail />} />
              <Route path="pagos" element={<AdminPayments />} />
              <Route path="eventos" element={<AdminEvents />} />
              <Route path="mensajes" element={<AdminMessages />} />
              <Route path="turnos" element={<AdminShifts />} />
              <Route path="asistencia" element={<AdminAttendance />} />
              <Route path="sitio-publico" element={<AdminPublicSite />} />
              <Route path="configuracion" element={<AdminSettings />} />
              <Route path="payment-return" element={<AdminPaymentReturn />} />
              <Route path="suscripcion/retorno" element={<AdminSubscriptionReturn />} />
            </Route>

            <Route path="/mi/login" element={<SubscriberLogin />} />
            <Route path="/mi/pago-retorno" element={<SubscriberPaymentReturn />} />
            <Route
              path="/mi"
              element={(
                <SubscriberProtectedRoute>
                  <SubscriberLayout />
                </SubscriberProtectedRoute>
              )}
            >
              <Route index element={<Navigate to="/mi/perfil" replace />} />
              <Route path="perfil" element={<SubscriberProfile />} />
              <Route path="progreso" element={<SubscriberProgress />} />
              <Route path="galeria" element={<SubscriberGallery />} />
              <Route path="peleas" element={<SubscriberFights />} />
              <Route path="pagos" element={<SubscriberPayments />} />
            </Route>
          </Routes>
        </BrowserRouter>
      </SubscriberAuthProvider>
      </SuperAuthProvider>
    </AuthProvider>
  );
}

export default App;
